.card {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-1-5);
	padding: var(--spacing-3);
	color: inherit;
	text-decoration: none;
	cursor: grab;
	background: var(--colour-background-surface);
	border: 1px solid var(--colour-border-subtle);
	border-radius: var(--radius-md);
	transition:
		border-color var(--motion-fast) var(--ease-standard),
		background-color var(--motion-fast) var(--ease-standard);

	&:active {
		cursor: grabbing;
	}

	/* Optimistic card — dim + non-draggable until the server row settles. */
	&.pending {
		cursor: default;
		opacity: 0.6;
	}

	/* Picked up: the source stays in its slot as a faded, dashed skeleton so you can
	   see where the card will drop — the lifted clone is the DragOverlay (.preview). */
	&.dragging {
		cursor: grabbing;
		border-color: var(--colour-border-strong);
		border-style: dashed;
		opacity: 0.4;
	}

	/* The floating clone under the cursor in the DragOverlay. */
	&.preview {
		cursor: grabbing;
		background: var(--colour-background-raised);
		border-color: var(--colour-border-strong);
		box-shadow: var(--shadow-lg);
		rotate: 2deg;
	}

	/* Elevation is the 1px border; hover lifts with a tint, not a heavy shadow. */
	&:hover {
		background: var(--colour-background-raised);
		border-color: var(--colour-border-strong);
	}

	& header {
		display: flex;
		gap: var(--spacing-2);
		align-items: center;
		justify-content: space-between;
		min-block-size: 1.25rem;
		padding: 0;
	}

	& .headerRight {
		position: relative;
		display: flex;
		gap: var(--spacing-1);
		align-items: center;

		/* Fades out under the "⋯" on hover so the two share the flush-right slot. */
		& > span {
			transition: opacity var(--motion-fast) var(--ease-standard);
		}
	}

	& .key {
		font-family: var(--font-family-mono);
		font-size: var(--font-size-xs);
		color: var(--colour-text-secondary);
		letter-spacing: var(--letter-spacing-wide);
	}

	/* "⋯" quick-actions trigger — hidden at rest, revealed on card hover, focus,
	   or while its menu is open. Keeps the resting board calm. */
	& .menuButton {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		inline-size: 1.5rem;
		block-size: 1.5rem;
		padding: 0;
		font-size: var(--font-size-base);
		line-height: 1;
		color: var(--colour-text-secondary);
		cursor: pointer;
		background: transparent;
		border: none;
		border-radius: var(--radius-sm);
		opacity: 0;
		transition:
			opacity var(--motion-fast) var(--ease-standard),
			background-color var(--motion-fast) var(--ease-standard);

		&:hover {
			background: var(--colour-background-muted);
		}

		&:disabled {
			display: none;
		}
	}

	&:hover .menuButton,
	& .menuButton:focus-visible,
	& .menuButton[data-state='open'] {
		opacity: 1;
	}

	/* The "⋯" reveals over the badge's slot, so hide the badge whenever it shows —
	   keeps the header flush-right at rest (no space reserved for the hidden button).
	   Mirrors the reveal above (:focus-visible, not :focus-within) so only genuine
	   keyboard focus counts; a mouse-driven close drops focus off the trigger (see
	   task-quick-menu) so the badge returns once the card is no longer hovered. */
	&:hover .headerRight > span,
	& .headerRight:has([data-state='open']) > span,
	& .headerRight:has(.menuButton:focus-visible) > span {
		opacity: 0;
	}

	/* The "⋯" floats out of flow, pinned to the header's right edge, so at rest the
	   badge sits flush-right (matching the key on the left). Size is shrunk below the
	   shared IconButton's 2rem — higher specificity beats .iconButton[data-size]. */
	& .headerRight .menuButton {
		position: absolute;
		inset-block-start: 50%;
		inset-inline-end: 0;
		inline-size: 1.5rem;
		block-size: 1.5rem;
		min-block-size: 0;
		transform: translateY(-50%);
	}

	/* Two-line clamp comes from the shared `.line-clamp` utility. */
	& .title {
		margin: 0;
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		line-height: var(--line-height-snug);
		color: var(--colour-text-primary);
	}

	& .titleInput {
		font-size: var(--font-size-sm);
	}

	& .meta {
		display: flex;
		gap: var(--spacing-2);
		align-items: center;
		justify-content: space-between;
	}

	& .labels {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-1);
		min-inline-size: 0;
	}

	/* Due date + assignee, grouped on the trailing edge. */
	& .metaRight {
		display: flex;
		flex: none;
		gap: var(--spacing-2);
		align-items: center;
	}

	& .checklist {
		display: inline-flex;
		gap: var(--spacing-0-5);
		align-items: center;
		font-size: var(--font-size-2xs);
		font-weight: var(--font-weight-semibold);
		color: var(--colour-text-tertiary);

		& svg {
			inline-size: 0.85rem;
			block-size: 0.85rem;
		}

		&.checklistDone {
			color: var(--colour-success);
		}
	}

	/* Quiet sub-task count — a small branch glyph + number, muted like the checklist. */
	& .subtasks {
		display: inline-flex;
		gap: var(--spacing-0-5);
		align-items: center;
		font-size: var(--font-size-2xs);
		font-weight: var(--font-weight-semibold);
		color: var(--colour-text-tertiary);

		& svg {
			inline-size: 0.85rem;
			block-size: 0.85rem;
		}
	}

	/* Blocked lock — the one signal that warms to danger, since it says "can't start yet". */
	& .blocked {
		display: inline-flex;
		align-items: center;
		color: var(--colour-danger);

		& svg {
			inline-size: 0.85rem;
			block-size: 0.85rem;
		}
	}

	/* Repeats — a quiet brand-tinted ↻, sitting with the other signals without shouting. */
	& .recurring {
		display: inline-flex;
		align-items: center;
		color: var(--colour-brand-text);

		& svg {
			inline-size: 0.85rem;
			block-size: 0.85rem;
		}
	}

	/* Between a drop and the next pointer move the card ignores the pointer, so its hover
	   state doesn't switch on under the cursor the instant the drag clone vanishes (TAS-69;
	   see hover-after-drop.ts). */
	:global(html[data-just-dropped]) & {
		pointer-events: none;
	}
}
